'use client';
import React, { useRef, useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { DialogHeader } from '@src/v3/components/coreUI/dialog';
import Button from '@src/v3/components/coreUI/button';
import TextField from '@src/v3/components/coreUI/textField';
import Icon from '@src/v3/components/coreUI/icon';
import GradientPanelSvg from '@src/v3/assets/svg/gradientPanel';
import AddressAutocomplete from '@src/v3/components/addressAutocomplete';
import UserProfileCard from './UserProfileCard';
import CropImage from '@src/v3/components/cropImage';
import { ConfirmationModal } from '@src/v3/components/confirmationModal';
import {
  RegisterStepProps,
  RegisterFormValues,
  UserType,
} from '@src/v3/interfaces/components/registerModal';
import { workDetailsValidationSchema } from '@src/v3/schemas/components/register/index';
import { REGISTER_STEPS } from '@src/v3/constants/configs';

const WorkDetailsStep: React.FC<RegisterStepProps> = ({
  savedCropState,
  setSavedCropState,
  onWorkDetailsSubmit,
  isWorkDetailsLoading = false,
  onStepChange,
  initialValues,
  userDetailsData,
  onCropModalStateChange,
  onSaveWorkDetails,
  workDetailsError,
  setWorkDetailsError,
  onClose,
  isDeletePhotoAllowed = true,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isCropModalOpen, setIsCropModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [tempFile, setTempFile] = useState<File | null>(null);
  const [existingImageSrc, setExistingImageSrc] = useState<string | null>(null);
  const [originalImageSrc, setOriginalImageSrc] = useState<string | null>(null); // Store original uncropped image

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (workDetailsError) {
      setErrorMessage(workDetailsError);
    }
  }, [workDetailsError]);

  useEffect(() => {
    if (initialValues?.companyPhotoOriginal) {
      setOriginalImageSrc(initialValues.companyPhotoOriginal);
    } else {
      setOriginalImageSrc(null);
    }
  }, [initialValues?.companyPhotoOriginal]);

  const handleSubmit = async (
    values: RegisterFormValues,
    // eslint-disable-next-line no-unused-vars
    { setSubmitting }: { setSubmitting: (_isSubmitting: boolean) => void }
  ) => {
    setErrorMessage(''); // Clear previous error
    try {
      await onWorkDetailsSubmit?.(values);
    } catch (error) {
      const errorMsg =
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Please fill in all required fields';
      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleEditPhoto = () => {
    if (originalImageSrc) {
      setExistingImageSrc(originalImageSrc);
      setTempFile(null);
      setIsCropModalOpen(true);
      onCropModalStateChange?.(true);
    } else {
      handlePhotoClick();
    }
  };

  const handlePhotoChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    _handleChange: (
      // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
      _e: React.ChangeEvent<HTMLInputElement>
    ) => void
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setTempFile(file);
      setExistingImageSrc(null);
      setIsCropModalOpen(true);
      onCropModalStateChange?.(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setOriginalImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropSuccess = (
    croppedImageBase64: string,
    handleChange: (
      // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
      _e: React.ChangeEvent<HTMLInputElement>
    ) => void,
    cropState?: { scale: number; crop: { x: number; y: number } },
    originalImage?: string
  ) => {
    handleChange({
      target: {
        name: 'companyPhoto',
        value: croppedImageBase64,
      },
    } as React.ChangeEvent<HTMLInputElement>);
    if (cropState) {
      setSavedCropState?.(cropState);
    }
    if (originalImage) {
      setOriginalImageSrc(originalImage);
    }
    setTempFile(null);
    setExistingImageSrc(null);
    setIsCropModalOpen(false);
  };

  const handleCropClose = () => {
    setIsCropModalOpen(false);
    setTempFile(null);
    setExistingImageSrc(null);
    onCropModalStateChange?.(false);
  };

  return (
    <Formik
      initialValues={{
        phone: initialValues?.phone || '',
        code: initialValues?.code || '',
        userType: initialValues?.userType || ('agent' as UserType),
        companyName: initialValues?.companyName || '',
        companyAddressLine1: initialValues?.companyAddressLine1 || '',
        companyAddressLine2: initialValues?.companyAddressLine2 || '',
        companyAddressPlaceId: initialValues?.companyAddressPlaceId || '',
        companyPhoto: initialValues?.companyPhoto || '',
        companyPhotoOriginal: initialValues?.companyPhotoOriginal || '',
      }}
      enableReinitialize={true}
      validationSchema={workDetailsValidationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        isSubmitting,
        submitForm,
      }) => {
        const handleCropSuccessWithChange = (
          croppedImageBase64: string,
          cropState?: { scale: number; crop: { x: number; y: number } },
          originalImage?: string
        ) => {
          handleCropSuccess(
            croppedImageBase64,
            handleChange,
            cropState,
            originalImage
          );
          if (originalImage) {
            setFieldValue('companyPhotoOriginal', originalImage);
          }
        };

        const handleBack = () => {
          if (onSaveWorkDetails) {
            onSaveWorkDetails(values);
          }
          setWorkDetailsError?.('');
          onStepChange?.(REGISTER_STEPS.USER_DETAILS);
        };

        return (
          <>
            <div className="h-full flex flex-col md:flex-row">
              <div className="md:w-[686px] w-full h-full md:h-[790px] self-stretch px-0 md:px-2.5 pt-0 md:pt-2.5 pb-0 md:pb-8 bg-white rounded-none md:rounded-l-2xl flex flex-col items-start relative">
                {/* Mobile Header */}
                <div className="md:hidden w-full">
                  <DialogHeader className="px-5 pt-6 pb-2 flex flex-col gap-3 items-start">
                    <div className="self-stretch flex flex-col gap-4 items-start w-full">
                      <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                        <div className="flex-1 justify-start text-black text-2xl font-medium font-geist leading-9 tracking-[-0.48px]">
                          Where do you work?
                        </div>
                        <span
                          onClick={onClose}
                          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                          <Icon
                            iconName="crossicon"
                            iconProps={{ className: 'w-5 h-5 text-gray-600' }}
                          />
                        </span>
                      </div>
                    </div>
                  </DialogHeader>
                </div>

                {/* Desktop Header */}
                <div className="hidden md:flex self-stretch flex-col justify-start items-start gap-2">
                  <div className="self-stretch h-auto md:h-32 px-8 pt-7 pb-5 bg-white flex flex-col justify-center items-start gap-3">
                    <div className="self-stretch inline-flex justify-start items-center gap-3">
                      <Button
                        type="button"
                        onClick={handleBack}
                        className="w-3.5 h-8 bg-white rounded-[36px] flex justify-center items-center gap-2 cursor-pointer  hover:bg-white "
                      >
                        <Icon
                          iconName="angleLeft"
                          iconProps={{ className: 'size-5 text-gray-1000' }}
                        />
                      </Button>
                    </div>
                    <div className="self-stretch flex flex-col justify-start items-start gap-4">
                      <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                        <div className="flex-1 justify-start text-gray-1000 text-2xl font-medium font-geist leading-9">
                          Where do you work
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <Form className="flex-1 w-full flex flex-col min-h-0 register-modal-form">
                  <div className="flex-1 w-full flex flex-col overflow-y-auto overflow-x-hidden pb-[100px] md:pb-0 min-h-0">
                    {/* Mobile Content */}
                    <div className="md:hidden px-5 py-3 flex flex-col gap-6">
                      {/* Photo Upload Section */}
                      <div className="flex flex-col justify-center items-center gap-3">
                        {values.companyPhoto ? (
                          <div
                            className="flex flex-col justify-center items-center relative rounded-[100px] w-[138px] h-[138px] p-4 gap-3 bg-[#FAFAFA] bg-cover bg-center bg-no-repeat overflow-visible"
                            style={{
                              backgroundImage: `url(${values.companyPhoto})`,
                            }}
                          >
                            <div
                              className={`absolute flex h-[33px] p-[10px] justify-center items-center gap-[10px] rounded-[100px] bg-[#EEEEEE] shadow-[0_99px_28px_0_rgba(0,0,0,0.00),0_64px_25px_0_rgba(0,0,0,0.00),0_36px_21px_0_rgba(0,0,0,0.02),0_16px_16px_0_rgba(0,0,0,0.03),0_4px_9px_0_rgba(0,0,0,0.03)] z-20 top-[0.5px] ${
                                isDeletePhotoAllowed
                                  ? ' -right-[37px] '
                                  : ' -right-[7px] '
                              }`}
                            >
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={handleEditPhoto}
                                className="w-5 h-5 flex items-center justify-center cursor-pointer hover:opacity-75 transition-opacity bg-transparent p-0 border-none outline-none focus:outline-none"
                              >
                                <Icon
                                  iconName="editDocument"
                                  iconProps={{
                                    className: 'w-5 h-5 text-[#343330]',
                                  }}
                                />
                              </Button>
                              {isDeletePhotoAllowed && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => {
                                    setIsDeleteModalOpen(true);
                                  }}
                                  className="w-5 h-5 flex items-center justify-center cursor-pointer hover:opacity-75 transition-opacity bg-transparent p-0 border-none outline-none focus:outline-none"
                                >
                                  <Icon
                                    iconName="deleteX"
                                    iconProps={{
                                      className: 'w-5 h-5 text-[#E5484D]',
                                    }}
                                  />
                                </Button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div
                            className="cursor-pointer flex flex-col justify-center items-center relative rounded-[100px] w-[138px] h-[138px] p-4 gap-3 bg-[#FAFAFA] overflow-visible"
                            onClick={handlePhotoClick}
                          >
                            <Button
                              type="button"
                              className="size-10 p-2.5 bg-gray-200 hover:bg-gray-200/80 rounded-[100px] inline-flex justify-center items-center gap-2.5 cursor-pointer transition-all z-10 border-none shadow-none"
                            >
                              <div className="size-5 relative">
                                <Icon
                                  iconName="plus"
                                  iconProps={{
                                    className: 'size-4 text-gray-1000',
                                  }}
                                />
                              </div>
                            </Button>
                            <div className="justify-start text-gray-1000 text-base font-medium font-geist leading-7 z-10 relative">
                              Upload photo
                            </div>
                          </div>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoChange(e, handleChange)}
                        />
                      </div>

                      {/* Company Name */}
                      <div className="flex flex-col gap-2 items-start w-full">
                        <div className="text-gray-800 text-sm font-normal font-geist leading-6 tracking-[-0.14px] w-full">
                          Company&apos;s Name
                        </div>
                        <TextField
                          value={values.companyName || ''}
                          error={touched.companyName && !!errors.companyName}
                          errorMessage={
                            touched.companyName ? errors.companyName : ''
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="companyName"
                          placeholder="Enter Company"
                          size="md"
                          variant="light"
                          disabled={isWorkDetailsLoading || isSubmitting}
                          inputClassName="!text-gray-1000 !text-sm leading-5"
                          showLabel={false}
                        />
                      </div>

                      {/* Company Address Line One */}
                      <div className="flex flex-col gap-2 items-start w-full">
                        <div className="text-gray-800 text-sm font-normal font-geist leading-6 tracking-[-0.14px] w-full">
                          Company Address (Line One)
                        </div>
                        <div className="w-full">
                          <AddressAutocomplete
                            defaultInputValue={values.companyAddressLine1 || ''}
                            value={values.companyAddressLine1 || ''}
                            isAllowGlobalSearch={false}
                            isError={
                              (touched.companyAddressLine1 &&
                                !!errors.companyAddressLine1) ||
                              (touched.companyAddressPlaceId &&
                                !!errors.companyAddressPlaceId)
                            }
                            helperText={(() => {
                              if (
                                touched.companyAddressLine1 &&
                                errors.companyAddressLine1
                              ) {
                                return errors.companyAddressLine1;
                              }
                              if (
                                touched.companyAddressPlaceId &&
                                errors.companyAddressPlaceId
                              ) {
                                return errors.companyAddressPlaceId;
                              }
                              return '';
                            })()}
                            onSelect={(address) => {
                              setFieldValue(
                                'companyAddressLine1',
                                address?.address || ''
                              );
                              setFieldValue(
                                'companyAddressPlaceId',
                                address?.place_id || ''
                              );
                            }}
                            wrapperClassName="address-autocomplete-wrapper zm-select-wrap zm-select-property-creation zeme-sign-up-address"
                          />
                        </div>
                      </div>

                      {/* Company Address Line Two */}
                      <div className="flex flex-col gap-2 items-start w-full">
                        <div className="text-gray-800 text-sm font-normal font-geist leading-6 tracking-[-0.14px] w-full">
                          Apartment, Unit, Floor, Etc.
                        </div>
                        <TextField
                          value={values.companyAddressLine2 || ''}
                          error={
                            touched.companyAddressLine2 &&
                            !!errors.companyAddressLine2
                          }
                          errorMessage={
                            touched.companyAddressLine2
                              ? errors.companyAddressLine2
                              : ''
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="companyAddressLine2"
                          placeholder="Enter Company Address (Line Two)"
                          size="md"
                          variant="light"
                          disabled={isWorkDetailsLoading || isSubmitting}
                          inputClassName="!text-gray-1000 !text-sm leading-5"
                          showLabel={false}
                        />
                      </div>

                      {/* Error Message - Mobile */}
                      {errorMessage && (
                        <div className="inline-flex justify-start items-start gap-2">
                          <Icon
                            iconName="warningCircle"
                            iconProps={{
                              className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                            }}
                          />
                          <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                            {errorMessage}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Desktop Content */}
                    <div className="hidden md:flex self-stretch px-8 py-2.5 flex-col justify-start items-start gap-6 md:gap-8">
                      <div className="self-stretch flex flex-col justify-center items-center gap-3">
                        {values.companyPhoto ? (
                          <div
                            className="flex flex-col justify-center items-center relative rounded-[100px] w-[138px] h-[138px] p-4 gap-3 bg-[#FAFAFA] bg-cover bg-center bg-no-repeat overflow-visible"
                            style={{
                              backgroundImage: `url(${values.companyPhoto})`,
                            }}
                          >
                            <div
                              className={`absolute flex h-[33px] p-[10px] justify-center items-center gap-[10px] rounded-[100px] bg-[#EEEEEE] shadow-[0_99px_28px_0_rgba(0,0,0,0.00),0_64px_25px_0_rgba(0,0,0,0.00),0_36px_21px_0_rgba(0,0,0,0.02),0_16px_16px_0_rgba(0,0,0,0.03),0_4px_9px_0_rgba(0,0,0,0.03)] z-20  top-[0.5px] ${
                                isDeletePhotoAllowed
                                  ? '-right-[37px]'
                                  : '-right-[7px]'
                              }`}
                            >
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={handleEditPhoto}
                                className="w-5 h-5 flex items-center justify-center cursor-pointer hover:opacity-75 transition-opacity bg-transparent p-0 border-none outline-none focus:outline-none"
                              >
                                <Icon
                                  iconName="editDocument"
                                  iconProps={{
                                    className: 'w-5 h-5 text-[#343330]',
                                  }}
                                />
                              </Button>
                              {isDeletePhotoAllowed && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => {
                                    setIsDeleteModalOpen(true);
                                  }}
                                  className="w-5 h-5 flex items-center justify-center cursor-pointer hover:opacity-75 transition-opacity bg-transparent p-0 border-none outline-none focus:outline-none"
                                >
                                  <Icon
                                    iconName="deleteX"
                                    iconProps={{
                                      className: 'w-5 h-5 text-[#E5484D]',
                                    }}
                                  />
                                </Button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div
                            className="cursor-pointer flex flex-col justify-center items-center relative rounded-[100px] w-[138px] h-[138px] p-4 gap-3 bg-[#FAFAFA] overflow-visible"
                            onClick={handlePhotoClick}
                          >
                            <Button
                              type="button"
                              className="size-10 p-2.5 bg-gray-200 hover:bg-gray-200/80 rounded-[100px] inline-flex justify-center items-center gap-2.5 cursor-pointer transition-all z-10 border-none shadow-none"
                            >
                              <div className="size-5 relative">
                                <Icon
                                  iconName="plus"
                                  iconProps={{
                                    className: 'size-4 text-gray-1000',
                                  }}
                                />
                              </div>
                            </Button>
                            <div className="justify-start text-gray-1000 text-base font-medium font-geist leading-7 z-10 relative">
                              Upload photo
                            </div>
                          </div>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoChange(e, handleChange)}
                        />
                      </div>

                      <div className="self-stretch flex flex-col justify-end items-center gap-6">
                        <div className="self-stretch flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6">
                            Company&apos;s Name
                          </div>
                          <TextField
                            value={values.companyName || ''}
                            error={touched.companyName && !!errors.companyName}
                            errorMessage={
                              touched.companyName ? errors.companyName : ''
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="companyName"
                            placeholder="Enter Company"
                            size="md"
                            variant="light"
                            disabled={isWorkDetailsLoading || isSubmitting}
                            inputClassName="!text-gray-1000 text-sm leading-5"
                            showLabel={false}
                          />
                        </div>

                        <div className="self-stretch inline-flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6">
                            Company Address (Line One)
                          </div>
                          <div className="self-stretch">
                            <AddressAutocomplete
                              defaultInputValue={
                                values.companyAddressLine1 || ''
                              }
                              value={values.companyAddressLine1 || ''}
                              isAllowGlobalSearch={false}
                              isError={
                                (touched.companyAddressLine1 &&
                                  !!errors.companyAddressLine1) ||
                                (touched.companyAddressPlaceId &&
                                  !!errors.companyAddressPlaceId)
                              }
                              helperText={(() => {
                                if (
                                  touched.companyAddressLine1 &&
                                  errors.companyAddressLine1
                                ) {
                                  return errors.companyAddressLine1;
                                }
                                if (
                                  touched.companyAddressPlaceId &&
                                  errors.companyAddressPlaceId
                                ) {
                                  return errors.companyAddressPlaceId;
                                }
                                return '';
                              })()}
                              onSelect={(address) => {
                                setFieldValue(
                                  'companyAddressLine1',
                                  address?.address || ''
                                );
                                setFieldValue(
                                  'companyAddressPlaceId',
                                  address?.place_id || ''
                                );
                              }}
                              wrapperClassName="address-autocomplete-wrapper zm-select-wrap zm-select-property-creation zeme-sign-up-address"
                            />
                          </div>
                        </div>

                        <div className="self-stretch flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6">
                            Apartment, Unit, Floor, Etc.
                          </div>
                          <TextField
                            value={values.companyAddressLine2 || ''}
                            error={
                              touched.companyAddressLine2 &&
                              !!errors.companyAddressLine2
                            }
                            errorMessage={
                              touched.companyAddressLine2
                                ? errors.companyAddressLine2
                                : ''
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="companyAddressLine2"
                            placeholder="Enter Company Address (Line Two)"
                            size="md"
                            variant="light"
                            disabled={isWorkDetailsLoading || isSubmitting}
                            inputClassName="!text-gray-1000 text-sm leading-5"
                            showLabel={false}
                          />
                        </div>
                      </div>

                      {/* Error Message - Desktop */}
                      {errorMessage && (
                        <div className="self-stretch inline-flex justify-start items-start gap-2">
                          <Icon
                            iconName="warningCircle"
                            iconProps={{
                              className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                            }}
                          />
                          <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                            {errorMessage}
                          </span>
                        </div>
                      )}

                      <Button
                        type="submit"
                        variant="primary"
                        size="xl"
                        className="self-stretch h-12 px-4 py-3 bg-focus rounded-[36px] inline-flex justify-center items-center gap-2"
                        disabled={isWorkDetailsLoading || isSubmitting}
                        isLoading={isWorkDetailsLoading || isSubmitting}
                        loadingType="dots"
                      >
                        <div className="justify-start text-white text-base font-medium font-geist leading-7">
                          Continue
                        </div>
                        <Icon
                          iconName="arrowRight"
                          iconProps={{ className: 'size-5 text-white' }}
                        />
                      </Button>
                    </div>
                  </div>
                </Form>

                {/* Mobile Bottom Navigation */}
                <div className="md:hidden absolute bottom-0 left-0 right-0 w-full px-5 py-5 bg-white shadow-[0px_-1px_20px_0px_rgba(0,0,0,0.05)] border-b border-gray-400 inline-flex justify-center items-start gap-6 z-10">
                  <Button
                    type="button"
                    onClick={handleBack}
                    className="h-12 px-8 py-2.5 bg-background-200 rounded-[36px] flex justify-start items-center gap-2.5 shrink-0"
                  >
                    <span className="justify-start text-gray-1000 text-base font-semibold font-geist">
                      Back
                    </span>
                  </Button>
                  <Button
                    type="button"
                    onClick={submitForm}
                    disabled={isWorkDetailsLoading || isSubmitting}
                    className="flex-1 h-12 px-8 py-2.5 bg-focus rounded-[36px] flex justify-center items-center min-w-0 disabled:opacity-50"
                  >
                    <span className="justify-start text-white text-base font-semibold font-geist">
                      Continue
                    </span>
                  </Button>
                </div>
              </div>
              <div className="hidden md:flex relative flex-col p-2.5 items-center justify-center gap-2.5 self-stretch shrink-0 rounded-r-2xl bg-background-200 overflow-hidden w-[550px] h-[790px]">
                <GradientPanelSvg
                  className="absolute inset-0 w-full h-full"
                  preserveAspectRatio="none"
                />
                {/* Center Card Component */}
                <UserProfileCard
                  firstName={userDetailsData?.firstName}
                  lastName={userDetailsData?.lastName}
                  companyName={values.companyName}
                  companyPhoto={values.companyPhoto}
                />
              </div>
            </div>
            {/* Crop Image Modal */}
            <CropImage
              isOpen={isCropModalOpen}
              file={tempFile}
              imageSrc={existingImageSrc}
              initialCropState={existingImageSrc ? savedCropState : null}
              onClose={handleCropClose}
              onSuccess={handleCropSuccessWithChange}
              onUpdatePhoto={handlePhotoClick}
            />
            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
              <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onOpenChange={setIsDeleteModalOpen}
                title="Do you want to delete?"
                message="This action cannot be undone. If you delete this file, you'll need to upload it again later if required."
                variant="danger"
                primaryButtonText="Delete"
                secondaryButtonText="Go Back"
                onPrimaryAction={() => {
                  setFieldValue('companyPhoto', '');
                  setFieldValue('companyPhotoOriginal', '');
                  setOriginalImageSrc(null);
                  setSavedCropState?.(null);
                  setIsDeleteModalOpen(false);
                }}
                onSecondaryAction={() => {
                  setIsDeleteModalOpen(false);
                }}
                showCloseButton={false}
                className="sm:!max-h-[280px] sm:!pt-8 sm:!px-6 "
              />
            )}
          </>
        );
      }}
    </Formik>
  );
};

export default WorkDetailsStep;
